import {
  EpicMoversParamType,
  EpicMoversRequestAndResponse,
  LiquidityAndSolvencyRatiosResponseElement,
  ProfitabilityRatiosResponse,
  ProfitabilityRatiosResponseElement,
} from '../../models/fundamental/types';
import { DEFAULT_PAGE_RESPONSE } from '../../models/pageable';

import { FundamentalState, SectorEnum } from './types';

export const DEFAULT_SEARCH_STOCK_LIMIT = 15;
export const SEARCH_STOCK_MAX_LIMIT = 60;

export const LAST_YEARS_COUNT_FOR_QUARTERS_DATA = -2;// -8 quarters
export const LAST_YEARS_COUNT_FOR_ANNUAL_DATA = -7;// -7 years
export const LAST_YEARS_COUNT_FOR_TTM_DATA = -1;// TTM means trailing 12 months

const DEFAULT_PROFITABILITY_TTM: ProfitabilityRatiosResponseElement = {
  grossProfitMargin: 0,
  operatingProfitMargin: 0,
  netProfitMargin: 0,
  returnOnAssets: 0,
  returnOnEquity: 0,
  dividendYield: 0,
  netIncomePerShare: 0,
  freeCashFlowPerShare: 0,
  bookValuePerShare: 0,
  roic: 0,
  eps: 0,
};

const DEFAULT_LIQUIDITY_AND_SOLVENCY_TTM: LiquidityAndSolvencyRatiosResponseElement = {
  currentRatio: 0,
  quickRatio: 0,
  cashRatio: 0,
  daysOfSalesOutstanding: 0,
  debtEquityRatio: 0,
  interestCoverage: 0,
  cashFlowToDebtRatio: 0,
};

const DEFAULT_VALUATION_TTM = {
  priceToBookRatio: 0,
  priceToSalesRatio: 0,
  priceEarningsRatio: 0,
  priceToFreeCashFlowsRatio: 0,
  priceEarningsToGrowthRatio: 0,
  enterpriseValueOverEbitda: 0,
  evToFreeCashFlow: 0,
};

export const DEFAULT_EPIC_MOVERS_DATA: EpicMoversRequestAndResponse = {
  params: {} as EpicMoversParamType,
  responseData: {
    topGainers: [],
    topLosers: [],
  },
};

export const DEFAULT_RATIOS_AND_FILTER_PARAMS = {
  profitability: {
    symbol: '',
    sector: '',
    ttm: DEFAULT_PROFITABILITY_TTM,
    sectorTtm: DEFAULT_PROFITABILITY_TTM,
    ratios: [],
    sectorRatios: [],
  } as ProfitabilityRatiosResponse,
  liquidityAndSolvency: {
    symbol: '',
    sector: '',
    ttm: DEFAULT_LIQUIDITY_AND_SOLVENCY_TTM,
    sectorTtm: DEFAULT_LIQUIDITY_AND_SOLVENCY_TTM,
    ratios: null,
    sectorRatios: null,
  },
  valuation: {
    symbol: '',
    sector: '',
    ttm: DEFAULT_VALUATION_TTM,
    sectorTtm: DEFAULT_VALUATION_TTM,
    ratios: [],
    sectorRatios: [],
  },
};

export const INITIAL_STATE: FundamentalState = {
  stockQuery: '',
  limit: DEFAULT_SEARCH_STOCK_LIMIT,
  stockResults: [],
  symbol: '',
  keyStats: [],
  // TODO: Stop using company name form companies after merging ART-1918 and use getCompanyLogoAndName for both mobile and web
  companies: [],
  companyLogoAndName: {},
  error: undefined,
  popularStocks: [],
  newStocks: [],
  dividendPayers: DEFAULT_PAGE_RESPONSE,
  highGrowthCompanies: DEFAULT_PAGE_RESPONSE,
  sectors: [],
  sectorDetails: {
    content: [],
    nextPageToRead: 0,
    sectorId: 0,
    sector: '',
  },
  topGainersAndLosersFilterValues: {
    minValue: 15,
    maxValue: 50,
  },
  IPOs: [],
  earningsSearchResponse: {
    content: [],
    totalPages: 0,
    totalElements: 0,
    size: 0,
    number: 0,
    numberOfElements: 0,
    sort: [],
    status: 0,
  },
  splits: [],
  statusByCall: {},
  availableIndexes: [],
  marketIndexes: [],
  ETFData: {
    nextPage: 0,
    totalElements: 0,
    content: [],
  },
  statementsData: {
    symbol: '',
    income: [],
    balanceSheet: [],
    cashFlow: [],
  },
  gappersData: { ...DEFAULT_PAGE_RESPONSE },
  week52HighLow: { ...DEFAULT_PAGE_RESPONSE },
  week52HighLowTopList: { topNHigh: [], topNLow: [] },
  ratiosData: DEFAULT_RATIOS_AND_FILTER_PARAMS,
  epicMoversData: DEFAULT_EPIC_MOVERS_DATA,
};

export const NAMES_SECTORS_CONTAINING_IMAGES: string[] = [
  SectorEnum.CommunicationsServices,
  SectorEnum.HealthCare,
  SectorEnum.ConsumerDiscretionary,
  SectorEnum.Technology,
  SectorEnum.Utilities,
  SectorEnum.ConsumerStaples,
  SectorEnum.Financials,
  SectorEnum.Materials,
  SectorEnum.RealEstate,
  SectorEnum.Industrials,
  SectorEnum.Energy,
];

export const WEEK_52_HIGH_LOW_TOP_LIST_COUNT = 4;
export const GET_FIRST_ITEM_SIZE = 1;
export const DEFAULT_PAGE = 0;
export const DEFAULT_PAGE_LIST_SIZE = 15;

/**
 * @deprecated Please use generic constant DEFAULT_PAGE_LIST_SIZE instead of this
 */
export const DEFAULT_SECTOR_DETAILS_SIZE = 15;
export const DEFAULT_SECTOR_PAGE = 0;
export const NO_MORE_PAGES_TO_READ = -1;

export const marketIndexesArray = [
  {
    symbol: '^GSPC',
    shownAs: 'S&P 500',
    indexName: 'S&P 500',
  },
  {
    symbol: '^NDX',
    shownAs: 'NASDAQ',
    indexName: 'NASDAQ 100',
  },
  {
    symbol: '^DJI',
    shownAs: 'DOW',
    indexName: 'Dow Jones Industrial Average',
  },
  {
    symbol: '^RUI',
    shownAs: 'Russell 1000',
    indexName: 'Russell 1000',
  },
  {
    symbol: '^VIX',
    shownAs: 'CBOE',
    indexName: 'CBOE Volatility Index',
  },
];

export const symbolSet = {
  RETAIL: 'RETAIL',
};

export const etfProjections = [ 'symbol', 'type', 'name' ].join(',');
export const etfDetailProjections = [ 'symbol', 'avg30Volume' ].join(',');
